import { useEffect, useState, useCallback, useRef } from "react";
import { MessageCircle, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ChavrutaPanel from "./ChavrutaPanel";

/**
 * ChavrutaBubble — the floating Chavruta entry point.
 *
 * Mounted once at the App layout level so it's reachable from every
 * page (reader, siddur, calendar, etc.) without each page having to
 * opt in. Visibility rules:
 *
 *   • Only renders when the user is authenticated. Anonymous visitors
 *     don't see the bubble because the underlying ask endpoint
 *     requires a logged-in user_id for quota tracking and history.
 *
 *   • Hidden on the auth page itself (`/auth`) — showing a Chavruta
 *     CTA on a sign-in form would be confusing.
 *
 * Style: navy fill, gold border, soft glow on hover. Bottom-left in
 * RTL so it sits where the eye naturally rests when reading
 * right-to-left Hebrew (the dominant content direction in Torahya).
 * Sits above the Siddur's bottom toolbar by stacking z-index, and
 * respects iOS safe-area insets via env(safe-area-inset-bottom).
 */
export default function ChavrutaBubble() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  // Listen for global "open chavruta with this context" events so
  // the Tanakh / Siddur readers can open the panel with a pre-filled
  // selection. We use a custom event rather than a context provider
  // to avoid wiring extra plumbing through every reader component.
  const pendingContextRef = useRef(null);

  useEffect(() => {
    const onOpen = (e) => {
      pendingContextRef.current = e.detail || null;
      setOpen(true);
    };
    window.addEventListener("chavruta:open", onOpen);
    return () => window.removeEventListener("chavruta:open", onOpen);
  }, []);

  // Memoised callback handed down to the panel so it can pull the
  // event payload (verse ref / selected text) for the next turn.
  // Declared BEFORE any early-return so React's hook order stays
  // stable — without this guard, switching auth states would trip
  // rules-of-hooks.
  const handleConsumeContext = useCallback(() => {
    const ctx = pendingContextRef.current;
    pendingContextRef.current = null;
    return ctx;
  }, []);

  // Hide the bubble on /auth and during initial page load when auth
  // state hasn't resolved yet — avoids a flash of the bubble for
  // anonymous visitors.
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (!user) return null;
  if (path.startsWith("/auth")) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="פתח חברותא"
          data-testid="chavruta-bubble"
          className="
            fixed z-40
            bottom-[max(1rem,env(safe-area-inset-bottom))] start-4
            h-14 w-14 sm:h-16 sm:w-16
            rounded-full
            bg-[#1a2b4a] text-[#f6e9c5]
            border-2 border-[#c9a449]/80
            shadow-[0_8px_24px_-8px_rgba(26,43,74,0.6)]
            hover:shadow-[0_12px_32px_-6px_rgba(201,164,73,0.4)]
            hover:scale-105
            active:scale-95
            transition-all duration-200
            flex items-center justify-center
            group
          "
        >
          {/* Soft inner ring on hover, drawn with absolute-positioned
              ::after via the parent's `group`. We use an actual <span>
              instead of a pseudo to stay in JSX. */}
          <span className="absolute inset-1 rounded-full border border-[#c9a449]/30 opacity-0 group-hover:opacity-100 transition-opacity" />
          <MessageCircle className="h-6 w-6 sm:h-7 sm:w-7 relative" strokeWidth={1.6} />
        </button>
      )}

      {open && (
        <ChavrutaPanel
          onClose={() => setOpen(false)}
          consumePendingContext={handleConsumeContext}
        />
      )}
    </>
  );
}


/**
 * Imperative helper for other components: dispatch a window event so
 * the bubble (mounted globally) opens with a pre-filled context. The
 * Tanakh/Siddur readers call this when the user clicks a
 * "Ask Chavruta about this" CTA off a text selection.
 *
 * Shape of `detail`:
 *   {
 *     ref?: "Genesis 1:1",          // explicit Sefaria ref
 *     selected_text?: "...",        // optional highlighted snippet
 *     initial_question?: "..."      // optional pre-typed question
 *   }
 */
export function openChavrutaWithContext(detail) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("chavruta:open", { detail }));
  } catch {
    // older browsers — fall back to a property-less open
    window.dispatchEvent(new CustomEvent("chavruta:open"));
  }
}

// Close { children: <button ... /> } export for the `X` icon — useful
// for any caller that wants to programmatically close the panel.
export { X as ChavrutaCloseIcon };
